import React from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from 'sonner';

// Contexts
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SettingsProvider, useSettings } from './contexts/SettingsContext';
import { OrganizationProvider, useOrganization } from './contexts/OrganizationContext';

// Layout Components
import { BottomNav } from './components/BottomNav';

// Pages - Landing & Auth
import LandingPage from './pages/LandingPage';
import CanopyGatewayPage from './pages/CanopyGatewayPage';
import CommercialLandingPage from './pages/CommercialLandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';

// Pages - Main App (New 6-section structure)
import ResidentHomePage from './pages/ResidentHomePage';
import WelcomePage from './pages/WelcomePage';
import DiningPage from './pages/DiningPage';
import WellnessPage from './pages/WellnessPage';
import ServicesPage from './pages/ServicesPage';
import CommunityPage from './pages/CommunityPage';
import LocalAreaPage from './pages/LocalAreaPage';
import FamilyGuestsPage from './pages/FamilyGuestsPage';
import GuestPortalPage from './pages/GuestPortalPage';
import MessagesPage from './pages/MessagesPage';

// Pages - Legacy/Existing
import HomePage from './pages/HomePage';
import OffersPage from './pages/OffersPage';
import EventsPage from './pages/EventsPage';
import CafePage from './pages/CafePage';
import MorePage from './pages/MorePage';
import MemberPage from './pages/MemberPage';
import PointsPage from './pages/PointsPage';
import MemberOffersPage from './pages/MemberOffersPage';
import ResidentsClubPage from './pages/ResidentsClubPage';
import ResidentsDashboardPage from './pages/ResidentsDashboardPage';
import ShopperDashboardPage from './pages/ShopperDashboardPage';
import ActivitiesPage from './pages/ActivitiesPage';
import MyBookingsPage from './pages/MyBookingsPage';
import CreditsPage from './pages/CreditsPage';
import TipsPage from './pages/TipsPage';
import TipDetailPage from './pages/TipDetailPage';
import PartnersPage from './pages/PartnersPage';
import VisitPage from './pages/VisitPage';
import AboutPage from './pages/AboutPage';
import HelpPage from './pages/HelpPage';
import AssistantPage from './pages/AssistantPage';
import TransportPage from './pages/TransportPage';

// Pages - Admin
import AdminPage from './pages/AdminPage';
import ManagerPage from './pages/ManagerPage';
import ManagerDiningServicesPage from './pages/ManagerDiningServicesPage';
import ManagerWelcomeGuidePage from './pages/ManagerWelcomeGuidePage';
import SuperAdminPage from './pages/SuperAdminPage';

// Protected Route Components
function ProtectedMemberRoute({ children }) {
    const { isAuthenticated, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }
    
    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }
    
    return children;
}

function ProtectedAdminRoute({ children }) {
    const { isAdmin, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }
    
    if (!isAdmin) {
        return <Navigate to="/" replace />;
    }
    
    return children;
}

function ProtectedManagerRoute({ children }) {
    const { isManager, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }
    
    if (!isManager) {
        return <Navigate to="/" replace />;
    }
    
    return children;
}

function ProtectedSuperAdminRoute({ children }) {
    const { isSuperAdmin, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }
    
    if (!isSuperAdmin) {
        return <Navigate to="/" replace />;
    }
    
    return children;
}

// Layout wrapper for pages with bottom nav
function PublicLayout({ children }) {
    return (
        <div className="min-h-screen bg-background">
            {children}
            <BottomNav />
        </div>
    );
}

// Main App Content
function AppContent() {
    const { seniorMode } = useSettings();
    
    return (
        <>
            {/* Senior Mode Indicator */}
            {seniorMode && (
                <div className="text-mode-indicator">
                    Large Text ON
                </div>
            )}
            
            <Routes>
                {/* Commercial Landing Page (main entry point for canopylifestyle.com) */}
                <Route path="/" element={<CommercialLandingPage />} />
                
                {/* Community/Organization Selection (access your community) */}
                <Route path="/community" element={<CanopyGatewayPage />} />
                
                {/* Guest Portal (public - no auth required) */}
                <Route path="/guest-portal/:accessCode" element={<GuestPortalPage />} />
                
                {/* Organization Landing Page (after selection) */}
                <Route path="/welcome" element={<LandingPage />} />
                
                {/* Auth Routes */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<PublicLayout><RegisterPage /></PublicLayout>} />
                
                {/* Main Resident Home (after login) */}
                <Route path="/home" element={
                    <ProtectedMemberRoute>
                        <PublicLayout><ResidentHomePage /></PublicLayout>
                    </ProtectedMemberRoute>
                } />
                
                {/* Retail Shopper Dashboard - PAUSED (keeping code for future use)
                <Route path="/shopper" element={
                    <ProtectedMemberRoute>
                        <PublicLayout><ShopperDashboardPage /></PublicLayout>
                    </ProtectedMemberRoute>
                } />
                */}
                
                {/* New 6-Section Navigation Routes */}
                <Route path="/events" element={<PublicLayout><EventsPage /></PublicLayout>} />
                <Route path="/dining" element={
                    <ProtectedMemberRoute>
                        <PublicLayout><DiningPage /></PublicLayout>
                    </ProtectedMemberRoute>
                } />
                <Route path="/wellness" element={
                    <ProtectedMemberRoute>
                        <PublicLayout><WellnessPage /></PublicLayout>
                    </ProtectedMemberRoute>
                } />
                <Route path="/services" element={
                    <ProtectedMemberRoute>
                        <PublicLayout><ServicesPage /></PublicLayout>
                    </ProtectedMemberRoute>
                } />
                <Route path="/transport" element={
                    <ProtectedMemberRoute>
                        <PublicLayout><TransportPage /></PublicLayout>
                    </ProtectedMemberRoute>
                } />
                <Route path="/community-hub" element={
                    <ProtectedMemberRoute>
                        <PublicLayout><CommunityPage /></PublicLayout>
                    </ProtectedMemberRoute>
                } />
                <Route path="/local" element={
                    <ProtectedMemberRoute>
                        <PublicLayout><LocalAreaPage /></PublicLayout>
                    </ProtectedMemberRoute>
                } />
                
                {/* Family & Guests */}
                <Route path="/family" element={
                    <ProtectedMemberRoute>
                        <PublicLayout><FamilyGuestsPage /></PublicLayout>
                    </ProtectedMemberRoute>
                } />
                
                {/* Messaging */}
                <Route path="/messages" element={
                    <ProtectedMemberRoute>
                        <MessagesPage />
                    </ProtectedMemberRoute>
                } />
                <Route path="/messages/:conversationId" element={
                    <ProtectedMemberRoute>
                        <MessagesPage />
                    </ProtectedMemberRoute>
                } />
                
                {/* Welcome & Onboarding */}
                <Route path="/welcome-guide" element={
                    <ProtectedMemberRoute>
                        <PublicLayout><WelcomePage /></PublicLayout>
                    </ProtectedMemberRoute>
                } />
                
                {/* Activities & Bookings (credit-based) */}
                <Route path="/activities" element={
                    <ProtectedMemberRoute>
                        <PublicLayout><ActivitiesPage /></PublicLayout>
                    </ProtectedMemberRoute>
                } />
                <Route path="/activities/:id" element={
                    <ProtectedMemberRoute>
                        <PublicLayout><ActivitiesPage /></PublicLayout>
                    </ProtectedMemberRoute>
                } />
                
                {/* Legacy Public Routes */}
                <Route path="/offers" element={<PublicLayout><OffersPage /></PublicLayout>} />
                <Route path="/cafe" element={<PublicLayout><CafePage /></PublicLayout>} />
                <Route path="/more" element={<PublicLayout><MorePage /></PublicLayout>} />
                <Route path="/tips" element={<PublicLayout><TipsPage /></PublicLayout>} />
                <Route path="/tips/:id" element={<PublicLayout><TipDetailPage /></PublicLayout>} />
                <Route path="/partners" element={<PublicLayout><PartnersPage /></PublicLayout>} />
                <Route path="/visit" element={<PublicLayout><VisitPage /></PublicLayout>} />
                <Route path="/about" element={<PublicLayout><AboutPage /></PublicLayout>} />
                <Route path="/help" element={<PublicLayout><HelpPage /></PublicLayout>} />
                <Route path="/notifications" element={
                    <ProtectedMemberRoute>
                        <PublicLayout><HelpPage /></PublicLayout>
                    </ProtectedMemberRoute>
                } />
                
                {/* Protected Member Routes */}
                <Route path="/member" element={
                    <ProtectedMemberRoute>
                        <PublicLayout><MemberPage /></PublicLayout>
                    </ProtectedMemberRoute>
                } />
                <Route path="/member/points" element={
                    <ProtectedMemberRoute>
                        <PublicLayout><PointsPage /></PublicLayout>
                    </ProtectedMemberRoute>
                } />
                <Route path="/member/offers" element={
                    <ProtectedMemberRoute>
                        <PublicLayout><MemberOffersPage /></PublicLayout>
                    </ProtectedMemberRoute>
                } />
                <Route path="/member/credits" element={
                    <ProtectedMemberRoute>
                        <PublicLayout><CreditsPage /></PublicLayout>
                    </ProtectedMemberRoute>
                } />
                <Route path="/member/bookings" element={
                    <ProtectedMemberRoute>
                        <PublicLayout><MyBookingsPage /></PublicLayout>
                    </ProtectedMemberRoute>
                } />
                
                {/* Residents Club Routes (legacy) */}
                <Route path="/residents-club" element={<PublicLayout><ResidentsClubPage /></PublicLayout>} />
                <Route path="/residents/dashboard" element={
                    <ProtectedMemberRoute>
                        <ResidentsDashboardPage />
                    </ProtectedMemberRoute>
                } />
                <Route path="/residents/activities" element={
                    <ProtectedMemberRoute>
                        <ActivitiesPage />
                    </ProtectedMemberRoute>
                } />
                <Route path="/residents/bookings" element={
                    <ProtectedMemberRoute>
                        <MyBookingsPage />
                    </ProtectedMemberRoute>
                } />
                <Route path="/residents/credits" element={
                    <ProtectedMemberRoute>
                        <CreditsPage />
                    </ProtectedMemberRoute>
                } />
                {/* Legacy redirect */}
                <Route path="/member/residents" element={<Navigate to="/residents/dashboard" replace />} />
                
                {/* AI Assistant Route */}
                <Route path="/assistant" element={
                    <ProtectedMemberRoute>
                        <AssistantPage />
                    </ProtectedMemberRoute>
                } />
                
                {/* Admin Route - No Bottom Nav */}
                <Route path="/admin" element={
                    <ProtectedAdminRoute>
                        <AdminPage />
                    </ProtectedAdminRoute>
                } />
                
                {/* Manager Route - No Bottom Nav */}
                <Route path="/manager" element={
                    <ProtectedManagerRoute>
                        <ManagerPage />
                    </ProtectedManagerRoute>
                } />
                
                {/* Manager Dining & Services CMS - Separate Page */}
                <Route path="/manager/dining-services" element={
                    <ProtectedManagerRoute>
                        <ManagerDiningServicesPage />
                    </ProtectedManagerRoute>
                } />
                
                {/* Manager Welcome Guide CMS - Separate Page */}
                <Route path="/manager/welcome-guide" element={
                    <ProtectedManagerRoute>
                        <ManagerWelcomeGuidePage />
                    </ProtectedManagerRoute>
                } />
                
                {/* Super Admin Route - No Bottom Nav */}
                <Route path="/super-admin" element={
                    <ProtectedSuperAdminRoute>
                        <SuperAdminPage />
                    </ProtectedSuperAdminRoute>
                } />
                
                {/* Catch all */}
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            
            {/* Toast notifications */}
            <Toaster 
                position="top-center" 
                richColors 
                toastOptions={{
                    style: {
                        fontSize: '1rem',
                        padding: '1rem 1.25rem',
                    }
                }}
            />
        </>
    );
}

function App() {
    return (
        <BrowserRouter>
            <OrganizationProvider>
                <AuthProvider>
                    <SettingsProvider>
                        <AppContent />
                    </SettingsProvider>
                </AuthProvider>
            </OrganizationProvider>
        </BrowserRouter>
    );
}

export default App;
